import React, { useState, useEffect } from 'react';

const InternshipApprovalDashboard = () => {
  const [interns, setInterns] = useState([]);
  const [filteredInterns, setFilteredInterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({
    collegeName: '',
    minCGPA: '',
    maxCGPA: '',
    searchTerm: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error("Failed to load config.json:", err));
  }, []);

  useEffect(() => {
    if (!config) return;

    const fetchInterns = async () => {
      try {
        const response = await fetch(`${config.BASE_URL}${config.FETCH_INTERNSHIP_APPLICATIONS}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch internship applications');
        }
        
        const result = await response.json();
        if (result.success) {
          const internsWithSelection = result.data.map(intern => ({
            ...intern,
            approved: false
          }));
          setInterns(internsWithSelection);
          setFilteredInterns(internsWithSelection);
        } else {
          throw new Error(result.message || 'Failed to fetch data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInterns();
  }, [config]);

  
  useEffect(() => {
    let result = [...interns];
    
    // Filter by college name
    if (filters.collegeName) {
      result = result.filter(intern => 
        intern.college && intern.college.toLowerCase().includes(filters.collegeName.toLowerCase())
      );
    }
    
    // Filter by minimum CGPA
    if (filters.minCGPA) {
      const min = parseFloat(filters.minCGPA);
      result = result.filter(intern => intern.cgpa && parseFloat(intern.cgpa) >= min);
    }
    
    // Filter by maximum CGPA
    if (filters.maxCGPA) {
      const max = parseFloat(filters.maxCGPA);
      result = result.filter(intern => intern.cgpa && parseFloat(intern.cgpa) <= max);
    }
    
    // Filter by search term (name, email, skills, etc.)
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(intern => 
        (intern.name && intern.name.toLowerCase().includes(term)) ||
        (intern.email && intern.email.toLowerCase().includes(term)) ||
        (intern.skills && intern.skills.toLowerCase().includes(term)) ||
        (intern.domain && intern.domain.toLowerCase().includes(term))
      );
    }
    
    setFilteredInterns(result);
  }, [filters, interns]);

  // Handle individual checkbox change
  const handleCheckboxChange = (internId) => {
    const updatedInterns = interns.map(intern => 
      intern.intern_id === internId ? { ...intern, approved: !intern.approved } : intern
    );
    setInterns(updatedInterns);
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setFilteredInterns(filteredInterns.map(intern => ({ ...intern, approved: newSelectAll })));
    // Also update the main interns array
    setInterns(interns.map(intern => ({ ...intern, approved: newSelectAll })));
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      collegeName: '',
      minCGPA: '',
      maxCGPA: '',
      searchTerm: ''
    });
    setShowFilters(false);
  };


  // Handle approval of selected interns
// Handle approval of selected interns with amount input
const handleApproveSelected = async () => {
  const selected = interns.filter(intern => intern.approved);
  if (selected.length === 0) {
    alert("Please select at least one intern to approve.");
    return;
  }

  // Ask for amount
  const amount = prompt("Enter the approval amount for selected interns:");
  if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
    alert("Please enter a valid amount.");
    return;
  }

  if (window.confirm(`Are you sure you want to approve ${selected.length} internship(s) with amount ₹${amount}?`)) {
    try {
      const response = await fetch(`${config.BASE_URL}${config.APPROVE_INTERNSHIP}`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({
          internIds: selected.map(intern => intern.intern_id),
          amount: parseFloat(amount)
        })
      });
      console.log("Sending internIds:", selected.map(i => i.intern_id), "Amount:", amount);

      const result = await response.json();
      if (result.success) {
        alert(`Approved ${selected.length} internship(s) successfully with amount ₹${amount}!`);
        // Remove approved interns from the list
        setInterns(prev =>
          prev.filter(intern =>
            !selected.some(sel => sel.intern_id === intern.intern_id)
          )
        );
      } else {
        throw new Error(result.message || 'Failed to approve interns');
      }
    } catch (err) {
      alert('Error approving interns: ' + err.message);
    }
  }
};


  
  // Handle rejection of selected interns
const handleRejectSelected = async () => {
  const selected = interns.filter(intern => intern.approved);
  if (selected.length === 0) {
    alert("Please select at least one intern to reject.");
    return;
  }

  const reason = prompt("Enter rejection reason:");
  if (!reason || reason.trim() === "") {
    alert("Rejection reason is required.");
    return;
  }

  if (window.confirm(`Are you sure you want to reject ${selected.length} internship(s)?`)) {
    try {
      const response = await fetch(`${config.BASE_URL}${config.REJECT_INTERNSHIP}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          internIds: selected.map(intern => intern.intern_id),
          reason: reason
        })
      });

      const result = await response.json();
      if (result.success) {
        alert(`Rejected ${selected.length} internship(s) successfully!`);
        // Remove rejected interns from the list
        setInterns(prev =>
          prev.filter(intern => !selected.some(sel => sel.intern_id === intern.intern_id))
        );
      } else {
        throw new Error(result.message || 'Failed to reject selected interns');
      }
    } catch (err) {
      alert('Error rejecting interns: ' + err.message);
    }
  }
};


  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading internship applications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h2>Error Loading Data</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="internship-approval-container">
      <header>
        <h1>Internship Approval Dashboard</h1>
        <div className="controls">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input 
              type="text" 
              placeholder="Search by name, email, skills..." 
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleFilterChange}
            />
          </div>
          <button 
            className={`filter-btn ${showFilters ? 'active' : ''}`} 
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="fas fa-filter"></i> Filters
          </button>
        </div>
      </header>

      {showFilters && (
        <div className="filter-panel">
          <h3>Filter Applications</h3>
          <div className="filter-grid">
            <div className="filter-group">
              <label>College Name</label>
              <input 
                type="text" 
                placeholder="Enter college name"
                name="collegeName"
                value={filters.collegeName}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Min CGPA</label>
              <input 
                type="number" 
                step="0.01"
                min="0"
                max="10"
                placeholder="0.00"
                name="minCGPA"
                value={filters.minCGPA}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Max CGPA</label>
              <input 
                type="number" 
                step="0.01"
                min="0"
                max="10"
                placeholder="10.00"
                name="maxCGPA"
                value={filters.maxCGPA}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      )}

      <div className="actions">
        <button className="approve-btn" onClick={handleApproveSelected}>
          <i className="fas fa-check"></i> Approve Selected
        </button>
        <button className="reject-btn" onClick={handleRejectSelected}>
          <i className="fas fa-times"></i> Reject Selected
        </button>
        <span className="selected-count">
          {interns.filter(intern => intern.approved).length} selected
        </span>
        <span className="filtered-count">
          Showing {filteredInterns.length} of {interns.length} applications
        </span>
      </div>

      <div className="table-container">
        {filteredInterns.length === 0 ? (
          <div className="no-data">
            <i className="fas fa-search"></i>
            <h3>No applications found</h3>
            <p>Try adjusting your filters or search terms.</p>
            <button onClick={clearFilters}>Clear all filters</button>
          </div>
        ) : (
          <table className="internship-table">
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox" 
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>ID</th>
                <th>Name & Photo</th>
                <th>Aadhar Card</th>
                <th>College Details</th>
                <th>Education</th>
                <th>Domain & Skills</th>
                <th>Duration</th>
                <th>Resume</th>
              </tr>
            </thead>
            <tbody>
              {filteredInterns.map(intern => (
                <tr key={intern.intern_id} className={intern.approved ? 'selected' : ''}>
                  <td>
                    <input 
                      type="checkbox" 
                      checked={intern.approved}
                      onChange={() => handleCheckboxChange(intern.intern_id)}
                    />
                  </td>
                  <td className="id-column">{intern.intern_id}</td>
                  <td className="name-column">
                    <div className="profile-info">
                      <img 
                        src={intern.profile_image || 'https://via.placeholder.com/40x40/cccccc/969696?text=No+Image'} 
                        alt={intern.name} 
                        className="profile-pic" 
                      />
                      <div>
                        <div>{intern.name}</div>
                        <div className="contact-info">{intern.email}</div>
                        <div className="contact-info">{intern.phone}</div>
                        <div className="contact-info">{intern.address}</div>
                      </div>
                    </div>
                  </td>
                  <td className="aadhar-column">
                    <div className="aadhar-images">
                      <img 
                        src={intern.aadhar_front || 'https://via.placeholder.com/60x40/4A90E2/FFFFFF?text=Aadhar+Front'} 
                        alt="Aadhar Front" 
                        onClick={() => window.open(intern.aadhar_front, "_blank")}
                      />
                      <img 
                        src={intern.aadhar_back || 'https://via.placeholder.com/60x40/4A90E2/FFFFFF?text=Aadhar+Back'} 
                        alt="Aadhar Back" 
                        onClick={() => window.open(intern.aadhar_back, "_blank")}
                      />
                    </div>
                    <div className="aadhar-number">{intern.aadhar}</div>
                  </td>
                  <td className="college-column">
                    <div><strong>{intern.college}</strong></div>
                    <div>{intern.branch}</div>
                    <div>CGPA: {intern.cgpa}</div>
                    <td className="id-column">
                      <a 
                        href={intern.college_id} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="resume-link"
                      >
                        View ID
                      </a>
                    </td>
                  </td>
                  <td className="education-column">
                    <div>
                      <strong>School:</strong> {intern.schoolName} ({intern.schoolPercentage}%)
                    </div>
                    <div>
                      <strong>Intermediate:</strong> {intern.intermediateName} ({intern.intermediatePercentage}%)
                    </div>
                  </td>
                  <td className="domain-column">
                    <div><strong>Domain:</strong> {intern.domain}</div>
                    <div><strong>Skills:</strong> {intern.skills}</div>
                  </td>
                  <td className="duration-column">{intern.duration}</td>
                  <td className="resume-column">
                    <a href={intern.resume} target="_blank" rel="noopener noreferrer" className="resume-link">
                      View Resume
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style jsx>{`
        .internship-approval-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background-color: #f5f7fa;
          min-height: 100vh;
        }
        
        header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 1px solid #ddd;
          flex-wrap: wrap;
        }
        
        h1 {
          color: #2c3e50;
          font-size: 28px;
          margin-bottom: 15px;
        }
        
        .controls {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        
        .search-box {
          position: relative;
          flex: 1;
          min-width: 250px;
        }
        
        .search-box input {
          width: 100%;
          padding: 10px 15px 10px 40px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 16px;
        }
        
        .search-box i {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #7f8c8d;
        }
        
        .filter-btn {
          background: #3498db;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .filter-btn.active {
          background: #2c3e50;
        }
        
        .filter-panel {
          background: white;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        
        .filter-panel h3 {
          margin-bottom: 15px;
          color: #2c3e50;
        }
        
        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
        }
        
        .filter-group label {
          margin-bottom: 5px;
          font-weight: 500;
          color: #2c3e50;
        }
        
        .filter-group input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        
        .filter-actions {
          display: flex;
          justify-content: flex-end;
        }
        
        .clear-filters {
          background: #95a5a6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .actions {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .approve-btn {
          background: #27ae60;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .reject-btn {
          background: #e74c3c;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .selected-count, .filtered-count {
          color: #7f8c8d;
          font-size: 14px;
        }
        
        .filtered-count {
          margin-left: auto;
        }
        
        .table-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          overflow-x: auto;
          min-height: 400px;
          display: flex;
          flex-direction: column;
        }
        
        .no-data {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #7f8c8d;
          text-align: center;
          flex: 1;
        }
        
        .no-data i {
          font-size: 48px;
          margin-bottom: 20px;
          color: #bdc3c7;
        }
        
        .no-data h3 {
          margin-bottom: 10px;
          color: #2c3e50;
        }
        
        .no-data button {
          margin-top: 15px;
          padding: 8px 16px;
          background: #3498db;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .internship-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .internship-table th,
        .internship-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }
        
        .internship-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #2c3e50;
          position: sticky;
          top: 0;
        }
        
        .internship-table tr:hover {
          background-color: #f1f7fd;
        }
        
        .internship-table tr.selected {
          background-color: #e1f5fe;
        }
        
        .profile-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .profile-pic {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: contain;
        }
        
        .contact-info {
          font-size: 12px;
          color: #7f8c8d;
        }
        
        .aadhar-images {
          display: flex;
          gap: 5px;
          margin-bottom: 5px;
        }
        
        .aadhar-images img {
          width: 60px;
          height: 40px;
          object-fit: cover;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .aadhar-number {
          font-size: 12px;
          color: #7f8c8d;
          word-break: break-all;
        }
        
        .resume-link {
          color: #3498db;
          text-decoration: none;
          font-weight: 500;
          padding: 6px 12px;
          border: 1px solid #3498db;
          border-radius: 4px;
          display: inline-block;
        }
        
        .resume-link:hover {
          background-color: #3498db;
          color: white;
        }
        
        .loading, .error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 50vh;
          text-align: center;
        }
        
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        
        .error button {
          margin-top: 20px;
          padding: 10px 20px;
          background: #3498db;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 1200px) {
          .internship-table {
            min-width: 1000px;
          }
          
          header {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default InternshipApprovalDashboard;